if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.skuFilteringEnabled = !!this.querySelector('.js-data-sku-media');
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.mediaGroupingEnabled || this.skuFilteringEnabled) {
        // Try to get the current variant ID for per-variant grouping
        const variantInput = this.section && this.section.querySelector('input[name="id"]');
        const variantId = variantInput ? parseInt(variantInput.value, 10) : null;
        const groupName = this.mediaGroupingEnabled ? this.getMediaGroupFromOptionSelectors() : null;
        this.setActiveMediaGroup(groupName, variantId);
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
        this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
        window.addEventListener('on:debounced-resize', this.zoomInitHandler);
        this.initZoom();
        this.zoomLinks.forEach((el) => {
          el.addEventListener('click', (evt) => {
            evt.preventDefault();
          });
        });
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled || this.skuFilteringEnabled) {
        const variantId = evt.detail.variant ? evt.detail.variant.id : null;
        const groupName = this.mediaGroupingEnabled ? this.getMediaGroupFromOptionSelectors() : null;
        this.setActiveMediaGroup(groupName, variantId);
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        this.customSetActiveMedia(variantMedia, true);
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets VIW (Variant Image Wizard) variant-to-media mapping if available.
     * @returns {?object} Map of variantId -> array of mediaIds, or null
     */
    getViwMapping() {
      if (typeof this.viwMapping === 'undefined') {
        this.viwMapping = null;
        if (typeof __productWizRioVariantImages !== 'undefined'
          && __productWizRioVariantImages
          && __productWizRioVariantImages.enabled
          && Array.isArray(__productWizRioVariantImages.mapping)) {
          this.viwMapping = {};
          __productWizRioVariantImages.mapping.forEach((entry) => {
            this.viwMapping[entry.id] = entry.media.map((m) => parseInt(m, 10));
          });
        }
      }
      return this.viwMapping;
    }

    /**
     * Gets SKU-to-media mapping by matching variant SKUs to image filenames.
     * @returns {?object} Map of variantId -> array of {mediaId, sortOrder}, or null
     */
    getSkuMapping() {
      if (typeof this.skuMapping === 'undefined') {
        this.skuMapping = null;
        const dataEl = this.querySelector('.js-data-sku-media');
        if (!dataEl) return null;

        const skuData = JSON.parse(dataEl.textContent);
        if (!skuData.variants.length || !skuData.media.length) return null;

        // Sort priority for image types
        const getSortOrder = (filename) => {
          if (/_co1|_co\d*\./.test(filename)) return 10;
          if (/_cutout/.test(filename)) return 10;
          if (/_sp\d/.test(filename)) return 15;
          if (/_ls1/.test(filename)) return 20;
          if (/_ls2/.test(filename)) return 30;
          if (/_ls3/.test(filename)) return 40;
          if (/_front/.test(filename)) return 50;
          if (/_top/.test(filename)) return 60;
          if (/_side/.test(filename)) return 70;
          if (/_pa\d/.test(filename)) return 75;
          if (/_ld|_v\d+_ld/.test(filename)) return 90;
          return 80;
        };

        // Build filename lookup
        const mediaFilenames = {};
        skuData.media.forEach((m) => {
          mediaFilenames[m.id] = m.filename;
        });

        // Match each media to a variant by SKU prefix in filename
        this.skuMapping = {};
        const matchedMediaIds = new Set();

        // Sort variants by SKU length descending so longer SKUs match first
        const sortedVariants = [...skuData.variants].sort((a, b) => b.sku.length - a.sku.length);

        sortedVariants.forEach((variant) => {
          const skuLower = variant.sku.toLowerCase();
          if (!skuLower) return;

          const matched = [];
          skuData.media.forEach((m) => {
            // Check if filename starts with SKU followed by _ or .
            const fname = m.filename;
            if (fname.startsWith(skuLower + '_') || fname.startsWith(skuLower + '.')) {
              if (!matchedMediaIds.has(m.id)) {
                matched.push({ mediaId: m.id, sortOrder: getSortOrder(fname) });
                matchedMediaIds.add(m.id);
              }
            }
          });

          if (matched.length > 0) {
            matched.sort((a, b) => a.sortOrder - b.sortOrder);
            this.skuMapping[variant.id] = matched.map((m) => m.mediaId);
          }
        });

        // Only use SKU mapping if it matched a reasonable number of variants
        const mappedCount = Object.keys(this.skuMapping).length;
        if (mappedCount < skuData.variants.length * 0.5) {
          this.skuMapping = null;
        }
      }
      return this.skuMapping;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {},
          variantGroups: {}
        };

        const variantMediaData = this.getMediaGroupData();
        this.viewerItems = this.querySelectorAll('.media-viewer__item');

        // Build variant-to-option mapping (variantId -> colour name)
        this.variantToOptionMap = {};
        if (variantMediaData && variantMediaData.variantMedia) {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            this.variantToOptionMap[variantMediaData.variantMedia[i].variantId] = variantMediaData.variantMedia[i].option;
          }
        }

        // Build a lookup of mediaId -> DOM item
        const mediaIdToItem = new Map();
        this.viewerItems.forEach((item) => {
          mediaIdToItem.set(parseInt(item.dataset.mediaId, 10), item);
        });

        // Try VIW mapping first, but validate it resolves to actual DOM items
        let viwMapping = this.getViwMapping();
        if (viwMapping) {
          let viwHits = 0;
          let viwTotal = 0;
          for (const mediaIds of Object.values(viwMapping)) {
            for (const mediaId of mediaIds) {
              viwTotal++;
              if (mediaIdToItem.has(mediaId)) viwHits++;
            }
          }
          // If less than half of VIW media IDs match DOM items, mapping is stale
          if (viwTotal > 0 && viwHits / viwTotal < 0.5) {
            viwMapping = null;
          }
        }
        const skuMapping = !viwMapping ? this.getSkuMapping() : null;
        const directMapping = viwMapping || skuMapping;
        if (directMapping) {
          for (const [variantId, mediaIds] of Object.entries(directMapping)) {
            const items = [];
            mediaIds.forEach((mediaId) => {
              const domItem = mediaIdToItem.get(mediaId);
              if (domItem) {
                const groupItem = { main: domItem };
                if (this.thumbs) {
                  groupItem.thumb = this.thumbs.querySelector(
                    `[data-media-id="${mediaId}"].media-thumbs__item`
                  );
                }
                items.push(groupItem);
              }
            });
            if (items.length > 0) {
              const optionName = this.variantToOptionMap[variantId] || '';
              this.mediaGroupMap.variantGroups[variantId] = {
                name: optionName,
                variantId: parseInt(variantId, 10),
                items: items
              };
            }
          }

          // Build per-option groups as union of variant groups with same option
          Object.values(this.mediaGroupMap.variantGroups).forEach((group) => {
            if (!this.mediaGroupMap.groups[group.name]) {
              this.mediaGroupMap.groups[group.name] = {
                name: group.name,
                items: []
              };
            }
            group.items.forEach((item) => {
              // Avoid duplicates in option group
              if (!this.mediaGroupMap.groups[group.name].items.some((existing) => existing.main === item.main)) {
                this.mediaGroupMap.groups[group.name].items.push(item);
              }
            });
          });
        } else if (variantMediaData && variantMediaData.variantMedia) {
          // Fallback: position-based grouping from theme's variant media data
          const featuredMediaMap = new Map();
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            const entry = variantMediaData.variantMedia[i];
            if (!featuredMediaMap.has(entry.mediaId)) {
              featuredMediaMap.set(entry.mediaId, entry);
            }
          }

          let currentVariantId = null;
          let currentMediaOptionName = false;

          this.viewerItems.forEach((item) => {
            const mediaId = parseInt(item.dataset.mediaId, 10);
            const entry = featuredMediaMap.get(mediaId);

            if (entry) {
              currentVariantId = entry.variantId;
              if (currentMediaOptionName !== entry.option) {
                currentMediaOptionName = entry.option;
              }
            }

            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }

            if (currentVariantId) {
              if (!this.mediaGroupMap.variantGroups[currentVariantId]) {
                this.mediaGroupMap.variantGroups[currentVariantId] = {
                  name: currentMediaOptionName,
                  variantId: currentVariantId,
                  items: []
                };
              }
              this.mediaGroupMap.variantGroups[currentVariantId].items.push(groupItem);
            }

            if (currentMediaOptionName) {
              if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
                this.mediaGroupMap.groups[currentMediaOptionName] = {
                  name: currentMediaOptionName,
                  items: []
                };
              }
              this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
            }
          });
        }

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Option group name (e.g. "Green")
     * @param {number} variantId - optional - Specific variant ID for per-variant filtering
     */
    setActiveMediaGroup(groupName, variantId) {
      const mediaGroupMap = this.getMediaGroupMap();

      // Try per-variant group first, fall back to per-option (colour) group
      let selectedGroup;
      if (variantId && mediaGroupMap.variantGroups[variantId]) {
        selectedGroup = mediaGroupMap.variantGroups[variantId];
      } else if (variantId && this.variantToOptionMap && this.variantToOptionMap[variantId]) {
        // Variant shares featured image with another variant -- use colour group
        selectedGroup = mediaGroupMap.groups[this.variantToOptionMap[variantId]];
      } else if (groupName) {
        selectedGroup = mediaGroupMap.groups[groupName];
      } else {
        selectedGroup = mediaGroupMap.groupFromItem(this.currentItem);
      }

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      if (theme.mediaMatches.md && this.dataset.layout === 'slider') {
        // Vertical thumb scrolling on desktop slider layout
        const scrollPos = this.thumbs.scrollTop;
        const lastVisibleThumbOffset = this.thumbs.clientHeight + scrollPos;
        const thumbOffset = thumb.offsetTop;

        if (thumbOffset + thumb.clientHeight > lastVisibleThumbOffset || thumbOffset < scrollPos) {
          this.thumbs.scrollTo({ top: thumbOffset, behavior: 'smooth' });
        }
      } else {
        // Horizontal thumb scrolling (default/mobile)
        const scrollPos = this.thumbs.scrollLeft;
        const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
        const thumbOffset = thumb.offsetLeft;

        if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
          this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
        }
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
